// Bói Đê tự do — flow cũ của trang /tong-hop, giữ nguyên bên trong hub Bói Đê:
// tạo phiên → tự gắn lá số Tử Vi/Bát Tự → gieo quẻ → chat. ChatPanel/MD được export
// để trang chủ đề Bói Đê tái dùng cho phần hỏi tiếp.
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Layers,
  Plus,
  Sparkles,
  Coins,
  Loader2,
  X,
  Send,
  MessageSquare,
  Eye,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import { MARKDOWN_REMARK_PLUGINS } from "@/lib/markdown";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DivinerPicker } from "@/components/diviner-picker";
import { TuviLaSoBoard } from "@/components/tuvi/tuvi-la-so-board";
import { HexagramFullBoard } from "@/components/iching/hexagram-full-board";
import { BaziBoard } from "@/components/bazi/bazi-board";
import { getDefaultDivinerId, useBoideUser } from "@/hooks/use-boide-user";
import { clampWords } from "@/lib/chat-limit";
import { useRegisterTour } from "@/components/tour/tour-provider";
import {
  listSessions,
  createSession,
  deleteSession,
  getSession,
  listTuviCharts,
  getTuviChart,
  deleteTuviChart,
  addSessionChart,
  removeSessionChart,
  listBaziCharts,
  getBaziChart,
  deleteBaziChart,
  addSessionBaziChart,
  removeSessionBaziChart,
  castHexagram,
  removeSessionHexagram,
  getChatHistory,
  sendChatMessage,
  SESSIONS_PAGE_SIZE,
  type SessionPage,
  type SessionSummary,
  type HexagramResult,
} from "@/lib/boide-api";
import { StarRating } from "@/components/star-rating";
import { AiWatermark } from "@/components/ai-watermark";
import { useChatRating } from "@/components/boi-de/use-chat-rating";
import { SilkDivider, SilkReadingMarkdown } from "@/components/boi-de/silk-reading";

export const MD = {
  strong: ({ children }: { children?: React.ReactNode }) => (
    <strong className="font-semibold text-primary">{children}</strong>
  ),
  h2: ({ children }: { children?: React.ReactNode }) => (
    <h2 className="font-serif-soft italic text-[#E5B869] text-lg mt-4 mb-2">{children}</h2>
  ),
  h3: ({ children }: { children?: React.ReactNode }) => (
    <h3 className="font-serif-soft italic text-[#E5B869] mt-3 mb-1.5">{children}</h3>
  ),
  ul: ({ children }: { children?: React.ReactNode }) => (
    <ul className="flex flex-col gap-1.5 my-2 list-none !pl-0">{children}</ul>
  ),
  li: ({ children }: { children?: React.ReactNode }) => (
    <li className="flex gap-2 items-start">
      <span className="text-[#E5B869] text-[12px] mt-[5px]">✦</span>
      <div className="flex-1">{children}</div>
    </li>
  ),
  p: ({ children }: { children?: React.ReactNode }) => (
    <p className="mb-2 last:mb-0 leading-7">{children}</p>
  ),
  ol: ({ children }: { children?: React.ReactNode }) => (
    <ol className="my-2 flex list-decimal flex-col gap-1.5 pl-5 marker:text-[#E5B869]">
      {children}
    </ol>
  ),
};

export function TongHopClassic({ initialSessionId }: { initialSessionId?: string }) {
  const [selectedId, setSelectedId] = useState<string | null>(initialSessionId ?? null);

  const justCreatedSessionRef = useRef(false);

  // Lọc "Bói Đê tự do" chạy ở SERVER (`kind: "tong_hop_free"`) chứ không lọc lại trên
  // một trang 10 dòng: người dùng nhiều phiên Kinh Dịch sẽ có trang đầu toàn phiên
  // Kinh Dịch, và danh sách này hiện trống dù họ có cả chục phiên Bói Đê.
  const [sessionsPage, setSessionsPage] = useState(1);
  const sessions = useQuery({
    queryKey: ["sessions", { page: sessionsPage, kind: "tong_hop_free" }],
    queryFn: () => listSessions({ page: sessionsPage, kind: "tong_hop_free" }),
    placeholderData: keepPreviousData,
  });

  // Register tour for Bói Đê tự do based on active session
  useRegisterTour(selectedId ? "tonghop-session" : "tonghop", !sessions.isLoading);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const scrollToStep = (tourId: string, stepIndex: number) => {
      let stepSelectors: string[] = [];
      if (tourId === "tonghop") {
        stepSelectors = [
          "body",
          '[data-tour="tonghop-subject-input"]',
          '[data-tour="tonghop-diviner-picker"]',
          '[data-tour="tonghop-create-btn"]',
          '[data-tour="tonghop-list"]',
        ];
      } else if (tourId === "tonghop-session") {
        stepSelectors = [
          "body",
          '[data-tour="tonghop-charts"]',
          '[data-tour="tonghop-bazi-charts"]',
          '[data-tour="tonghop-hexagrams"]',
          '[data-tour="tonghop-chat"]',
        ];
      }

      const selector = stepSelectors[stepIndex];
      if (!selector || selector === "body") return;

      const container = document.querySelector("main");

      const isVisible = (el: HTMLElement) => {
        const rect = el.getBoundingClientRect();
        const style = window.getComputedStyle(el);
        return (
          rect.width > 0 &&
          rect.height > 0 &&
          style.display !== "none" &&
          style.visibility !== "hidden"
        );
      };

      const pickVisibleElement = (sel: string): HTMLElement | null => {
        const elements = Array.from(document.querySelectorAll(sel));
        for (const el of elements) {
          if (el instanceof HTMLElement && isVisible(el)) {
            return el;
          }
        }
        const first = document.querySelector(sel);
        return first instanceof HTMLElement ? first : null;
      };

      const target = pickVisibleElement(selector);

      if (container instanceof HTMLElement && target instanceof HTMLElement) {
        const containerRect = container.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();
        const relativeTop = targetRect.top - containerRect.top + container.scrollTop;

        container.scrollTo({
          top: Math.max(0, relativeTop - 24),
          behavior: "auto",
        });

        window.dispatchEvent(new Event("resize"));

        const interval = setInterval(() => {
          window.dispatchEvent(new Event("resize"));
        }, 50);
        setTimeout(() => {
          clearInterval(interval);
          window.dispatchEvent(new Event("resize"));
        }, 300);
      }
    };

    const handleTourEvent = (e: Event) => {
      const { tourId, stepIndex, type } = (e as CustomEvent).detail || {};
      if (tourId !== "tonghop" && tourId !== "tonghop-session") return;
      if (type === "step:after" || type === "tour:start" || type === "step:before") {
        scrollToStep(tourId, stepIndex);
      }
    };

    const handlePrescroll = (e: Event) => {
      const { tourId, stepIndex } = (e as CustomEvent).detail || {};
      if (tourId !== "tonghop" && tourId !== "tonghop-session") return;
      scrollToStep(tourId, stepIndex);
    };

    window.addEventListener("tour-event", handleTourEvent);
    window.addEventListener("tour-prescroll", handlePrescroll);
    return () => {
      window.removeEventListener("tour-event", handleTourEvent);
      window.removeEventListener("tour-prescroll", handlePrescroll);
    };
  }, [selectedId]);

  return (
    <>
      {selectedId ? (
        <TongHopSession sessionId={selectedId} onBack={() => setSelectedId(null)} />
      ) : (
        <SessionPicker
          onPick={setSelectedId}
          sessions={sessions}
          justCreatedRef={justCreatedSessionRef}
          page={sessionsPage}
          onPageChange={setSessionsPage}
        />
      )}
    </>
  );
}

/**
 * Trần lá số cho hai panel gắn lá số vào phiên (`ChartsPanel`, `BaziChartsPanel`).
 *
 * 🔴 **KHÔNG phải 10 như các trang lịch sử.** Hai panel ấy vừa là ô chọn vừa là chỗ tra
 * TÊN cho lá số đã gắn vào phiên; lá số đã gắn mà rơi sang trang sau thì badge của nó
 * biến mất khỏi phiên dù nó vẫn đang được luận. 100 là trần BE cho phép.
 */
const CHART_PICKER_LIMIT = 100;

// Tên phiên mặc định: "Bói Đê dd/mm/yyyy" — cũng là tên hiển thị trong Lịch sử.
function defaultSubject(): string {
  return `Bói Đê ${new Date().toLocaleDateString("vi-VN")}`;
}

// ─── Danh sách / tạo phiên ──────────────────────────────────────────────────
function SessionPicker({
  onPick,
  sessions,
  justCreatedRef,
  page,
  onPageChange,
}: {
  onPick: (id: string) => void;
  sessions: UseQueryResult<SessionPage, Error>;
  justCreatedRef: React.MutableRefObject<boolean>;
  page: number;
  onPageChange: (page: number) => void;
}) {
  const { user } = useBoideUser();
  const qc = useQueryClient();
  const { confirm, confirmDialog } = useConfirm();
  const [subject, setSubject] = useState(defaultSubject());
  const [divinerId, setDivinerId] = useState("cu_an");
  useEffect(() => {
    if (user?.profile) setDivinerId(getDefaultDivinerId(user.profile));
  }, [user]);
  // Chỉ hiện phiên Bói Đê TỰ DO — server đã lọc bằng `kind: "tong_hop_free"`
  // (phiên Bói Đê theo chủ đề có metadata.boi_de riêng).
  const tongHop = sessions.data?.items ?? [];
  const tongHopTotal = sessions.data?.total ?? 0;
  const tongHopPages = Math.max(1, Math.ceil(tongHopTotal / SESSIONS_PAGE_SIZE));

  const create = useMutation({
    mutationFn: () =>
      createSession({
        subject: subject.trim() || defaultSubject(),
        diviner_id: divinerId,
        fortune_type: "tong_hop",
      }),
    onSuccess: (res) => {
      justCreatedRef.current = true;
      qc.invalidateQueries({ queryKey: ["sessions"] });
      onPick(res.session_id);
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Không tạo được phiên"),
  });

  const del = useMutation({
    mutationFn: (id: string) => deleteSession(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sessions"] });
      toast.success("Đã xoá phiên");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Không xoá được phiên"),
  });

  return (
    <div className="space-y-5">
      <Card className="border-gold-soft/40 bg-card/60">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Plus className="h-4 w-4 text-primary" /> Tạo phiên Bói Đê mới
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div data-tour="tonghop-subject-input">
            <label className="mb-1.5 block text-xs text-muted-foreground">
              Tên phiên (sửa được)
            </label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="VD: Sự nghiệp & tình duyên 2026"
            />
          </div>
          <div data-tour="tonghop-diviner-picker">
            <label className="mb-1.5 block text-xs text-muted-foreground">Chọn thầy</label>
            <DivinerPicker value={divinerId} onChange={setDivinerId} />
          </div>
          <Button
            data-tour="tonghop-create-btn"
            onClick={() => create.mutate()}
            disabled={subject.trim().length < 3 || create.isPending}
            className="w-full bg-gold-gradient text-primary-foreground"
          >
            {create.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Plus className="mr-2 h-4 w-4" />
            )}
            Tạo phiên
          </Button>
        </CardContent>
      </Card>

      <Card className="border-gold-soft/40 bg-card/60">
        <CardHeader className="pb-3" data-tour="tonghop-list">
          <CardTitle className="flex items-center gap-2 text-base">
            <Layers className="h-4 w-4 text-primary" /> Phiên Bói Đê của bạn
          </CardTitle>
        </CardHeader>
        <CardContent>
          {sessions.isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full rounded-xl" />
              ))}
            </div>
          ) : tongHop.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              Chưa có phiên nào. Tạo phiên mới ở trên để bắt đầu.
            </p>
          ) : (
            <ul className="space-y-2">
              {tongHop.map((s: SessionSummary) => (
                <li key={s.id} className="flex items-center gap-2">
                  <button
                    onClick={() => onPick(s.id)}
                    className="flex min-w-0 flex-1 items-center gap-3 rounded-xl border border-border/40 bg-background/40 px-3 py-2.5 text-left transition hover:border-primary/40"
                  >
                    <Layers className="h-4 w-4 shrink-0 text-primary" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">{s.subject}</div>
                      <div className="text-xs text-muted-foreground">
                        {s.metadata?.chart_ids?.length ?? 0} Tử Vi ·{" "}
                        {s.metadata?.bazi_chart_ids?.length ?? 0} Bát Tự ·{" "}
                        {s.metadata?.hexagrams_count ?? s.metadata?.hexagrams?.length ?? 0} quẻ
                      </div>
                    </div>
                  </button>
                  <button
                    type="button"
                    disabled={del.isPending}
                    aria-label="Xoá phiên"
                    title="Xoá phiên"
                    onClick={async () => {
                      const ok = await confirm({
                        title: "Xoá phiên Bói Đê này?",
                        description: (
                          <>
                            Phiên “{s.subject}” và toàn bộ lịch sử đàm đạo trong đó sẽ bị xoá. Hành
                            động này không thể hoàn tác.
                          </>
                        ),
                        confirmLabel: "Xoá phiên",
                      });
                      if (ok) del.mutate(s.id);
                    }}
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-destructive/40 bg-background/40 text-destructive/70 transition hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {tongHopPages > 1 && (
            <nav
              aria-label="Phân trang phiên Bói Đê"
              className="mt-3 flex items-center justify-between gap-2 text-xs text-muted-foreground"
            >
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1 || sessions.isFetching}
                onClick={() => onPageChange(Math.max(1, page - 1))}
              >
                Trước
              </Button>
              <span aria-live="polite">
                Trang {Math.min(page, tongHopPages)}/{tongHopPages} · {tongHopTotal} phiên
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= tongHopPages || sessions.isFetching}
                onClick={() => onPageChange(Math.min(tongHopPages, page + 1))}
              >
                Sau
              </Button>
            </nav>
          )}
        </CardContent>
      </Card>
      {confirmDialog}
    </div>
  );
}

// ─── Một phiên Bói Đê ──────────────────────────────────────────────────────
function TongHopSession({ sessionId, onBack }: { sessionId: string; onBack: () => void }) {
  const session = useQuery({
    queryKey: ["session", sessionId],
    queryFn: () => getSession(sessionId),
  });

  return (
    <div className="space-y-4">
      <button onClick={onBack} className="text-sm text-muted-foreground hover:text-foreground">
        ← Về danh sách phiên
      </button>
      <div className="text-lg font-semibold text-foreground">{session.data?.subject}</div>
      <ChartsPanel sessionId={sessionId} chartIds={session.data?.metadata?.chart_ids ?? []} />
      <BaziChartsPanel
        sessionId={sessionId}
        chartIds={session.data?.metadata?.bazi_chart_ids ?? []}
      />
      <CastPanel
        sessionId={sessionId}
        hexagrams={(session.data?.metadata?.hexagrams as HexagramResult[]) ?? []}
      />
      <ChatPanel sessionId={sessionId} />
    </div>
  );
}

function ChartsPanel({ sessionId, chartIds }: { sessionId: string; chartIds: string[] }) {
  const qc = useQueryClient();
  const { confirm, confirmDialog } = useConfirm();
  const [openChartId, setOpenChartId] = useState<string | null>(null);
  // ⚠️ Panel này vừa là ô chọn vừa là chỗ TRA TÊN cho lá số đã gắn vào phiên
  // (`chartIds`), nên lấy trần lớn nhất BE cho phép thay vì 10 như các trang lịch sử:
  // lá số đã gắn mà rơi sang trang sau thì badge của nó biến mất khỏi phiên.
  const charts = useQuery({
    queryKey: ["tuvi-charts", { page: 1, limit: CHART_PICKER_LIMIT }],
    queryFn: () => listTuviCharts(1, CHART_PICKER_LIMIT),
  });
  const all = charts.data?.items ?? [];
  const attached = all.filter((c) => chartIds.includes(c.id));
  const available = all.filter((c) => !chartIds.includes(c.id));

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["session", sessionId] });
  };
  const add = useMutation({
    mutationFn: (chartId: string) => addSessionChart(sessionId, chartId),
    onSuccess: refresh,
    onError: (e) => toast.error(e instanceof Error ? e.message : "Không gắn được lá số"),
  });
  const remove = useMutation({
    mutationFn: (chartId: string) => removeSessionChart(sessionId, chartId),
    onSuccess: refresh,
    onError: (e) => toast.error(e instanceof Error ? e.message : "Không bỏ được lá số"),
  });
  const del = useMutation({
    mutationFn: (chartId: string) => deleteTuviChart(chartId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tuvi-charts"] });
      refresh();
      toast.success("Đã xoá lá số");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Không xoá được lá số"),
  });

  return (
    <Card className="border-gold-soft/40 bg-card/60">
      <CardHeader className="pb-3" data-tour="tonghop-charts">
        <CardTitle className="flex items-center gap-2 text-base">
          <Sparkles className="h-4 w-4 text-primary" /> Lá số Tử Vi trong phiên
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {attached.length === 0 ? (
          <p className="text-xs text-muted-foreground">Chưa gắn lá số Tử Vi nào.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {attached.map((c) => (
              <Badge key={c.id} className="gap-1.5 bg-gold-gradient/15 text-foreground">
                <button
                  onClick={() => setOpenChartId(c.id)}
                  className="inline-flex items-center gap-1 hover:underline"
                >
                  <Eye className="h-3 w-3" /> {c.ho_ten} ({c.nam_sinh})
                </button>
                <button onClick={() => remove.mutate(c.id)} disabled={remove.isPending}>
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}
        {openChartId ? (
          <ChartDetailDialog chartId={openChartId} onClose={() => setOpenChartId(null)} />
        ) : null}
        {available.length > 0 ? (
          <div>
            <div className="mb-1.5 text-xs text-muted-foreground">Thêm lá số Tử Vi đã lập:</div>
            <div className="flex flex-wrap gap-2">
              {available.map((c) => (
                <div
                  key={c.id}
                  className="inline-flex items-center overflow-hidden rounded-md border border-input"
                >
                  <Button
                    size="sm"
                    variant="ghost"
                    className="rounded-none border-0"
                    disabled={add.isPending}
                    onClick={() => add.mutate(c.id)}
                  >
                    <Plus className="mr-1 h-3 w-3" /> {c.ho_ten}
                  </Button>
                  <button
                    type="button"
                    disabled={del.isPending}
                    aria-label="Xoá lá số"
                    title="Xoá lá số"
                    onClick={async () => {
                      const ok = await confirm({
                        title: "Xoá lá số Tử Vi này?",
                        description: (
                          <>
                            Lá số “{c.ho_ten || "Ẩn danh"}” và phần khai mở đã trả tiền sẽ bị xoá ở
                            mọi nơi. Hành động này không thể hoàn tác và không hoàn tiền.
                          </>
                        ),
                        confirmLabel: "Xoá lá số",
                      });
                      if (ok) del.mutate(c.id);
                    }}
                    className="flex h-8 items-center border-l border-input px-2 text-destructive/70 transition hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </CardContent>
      {confirmDialog}
    </Card>
  );
}

function BaziChartsPanel({ sessionId, chartIds }: { sessionId: string; chartIds: string[] }) {
  const qc = useQueryClient();
  const { confirm, confirmDialog } = useConfirm();
  const [openChartId, setOpenChartId] = useState<string | null>(null);
  // Cùng lý do với `ChartsPanel` — xem chú thích ở đó.
  const charts = useQuery({
    queryKey: ["bazi-charts", { page: 1, limit: CHART_PICKER_LIMIT }],
    queryFn: () => listBaziCharts(1, CHART_PICKER_LIMIT),
  });
  const all = charts.data?.items ?? [];
  const attached = all.filter((c) => chartIds.includes(c.id));
  const available = all.filter((c) => !chartIds.includes(c.id));

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["session", sessionId] });
  };
  const add = useMutation({
    mutationFn: (chartId: string) => addSessionBaziChart(sessionId, chartId),
    onSuccess: refresh,
    onError: (e) => toast.error(e instanceof Error ? e.message : "Không gắn được lá số Bát Tự"),
  });
  const remove = useMutation({
    mutationFn: (chartId: string) => removeSessionBaziChart(sessionId, chartId),
    onSuccess: refresh,
    onError: (e) => toast.error(e instanceof Error ? e.message : "Không bỏ được lá số Bát Tự"),
  });
  const del = useMutation({
    mutationFn: (chartId: string) => deleteBaziChart(chartId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["bazi-charts"] });
      refresh();
      toast.success("Đã xoá lá số");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Không xoá được lá số Bát Tự"),
  });

  return (
    <Card className="border-gold-soft/40 bg-card/60">
      <CardHeader className="pb-3" data-tour="tonghop-bazi-charts">
        <CardTitle className="flex items-center gap-2 text-base">
          <Sparkles className="h-4 w-4 text-primary" /> Lá số Bát Tự trong phiên
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {attached.length === 0 ? (
          <p className="text-xs text-muted-foreground">Chưa gắn lá số Bát Tự nào.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {attached.map((c) => (
              <Badge key={c.id} className="gap-1.5 bg-gold-gradient/15 text-foreground">
                <button
                  onClick={() => setOpenChartId(c.id)}
                  className="inline-flex items-center gap-1 hover:underline"
                >
                  <Eye className="h-3 w-3" /> {c.ho_ten} ({c.nam_sinh})
                </button>
                <button onClick={() => remove.mutate(c.id)} disabled={remove.isPending}>
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}
        {openChartId ? (
          <BaziChartDetailDialog chartId={openChartId} onClose={() => setOpenChartId(null)} />
        ) : null}
        {available.length > 0 ? (
          <div>
            <div className="mb-1.5 text-xs text-muted-foreground">Thêm lá số Bát Tự đã lập:</div>
            <div className="flex flex-wrap gap-2">
              {available.map((c) => (
                <div
                  key={c.id}
                  className="inline-flex items-center overflow-hidden rounded-md border border-input"
                >
                  <Button
                    size="sm"
                    variant="ghost"
                    className="rounded-none border-0"
                    disabled={add.isPending}
                    onClick={() => add.mutate(c.id)}
                  >
                    <Plus className="mr-1 h-3 w-3" /> {c.ho_ten}
                  </Button>
                  <button
                    type="button"
                    disabled={del.isPending}
                    aria-label="Xoá lá số Bát Tự"
                    title="Xoá lá số Bát Tự"
                    onClick={async () => {
                      const ok = await confirm({
                        title: "Xoá lá số Bát Tự này?",
                        description: (
                          <>
                            Lá số “{c.ho_ten || "Ẩn danh"}” và phần khai mở đã trả tiền sẽ bị xoá ở
                            mọi nơi. Hành động này không thể hoàn tác và không hoàn tiền.
                          </>
                        ),
                        confirmLabel: "Xoá lá số",
                      });
                      if (ok) del.mutate(c.id);
                    }}
                    className="flex h-8 items-center border-l border-input px-2 text-destructive/70 transition hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </CardContent>
      {confirmDialog}
    </Card>
  );
}

function BaziChartDetailDialog({ chartId, onClose }: { chartId: string; onClose: () => void }) {
  const chart = useQuery({
    queryKey: ["bazi-chart", chartId],
    queryFn: () => getBaziChart(chartId),
  });
  const c = chart.data;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="block max-h-[88vh] space-y-4 overflow-y-auto overflow-x-hidden sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="text-gold-gradient">Lá số Bát Tự: {c?.ho_ten ?? "…"}</DialogTitle>
        </DialogHeader>
        {chart.isLoading || !c ? (
          <Skeleton className="h-72 w-full rounded-xl" />
        ) : (
          <BaziBoard chart={c} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function CastPanel({ sessionId, hexagrams }: { sessionId: string; hexagrams: HexagramResult[] }) {
  const qc = useQueryClient();
  const [openHex, setOpenHex] = useState<HexagramResult | null>(null);
  const cast = useMutation({
    mutationFn: () => castHexagram(sessionId),
    onSuccess: (res) => {
      toast.success(`Đã gieo quẻ: ${res.hexagram_result?.que_chinh?.ten ?? ""}`);
      qc.invalidateQueries({ queryKey: ["session", sessionId] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Gieo quẻ thất bại"),
  });
  const remove = useMutation({
    mutationFn: (index: number) => removeSessionHexagram(sessionId, index),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["session", sessionId] }),
    onError: (e) => toast.error(e instanceof Error ? e.message : "Không bỏ được quẻ"),
  });

  return (
    <Card className="border-gold-soft/40 bg-card/60">
      <CardHeader className="pb-3" data-tour="tonghop-hexagrams">
        <CardTitle className="flex items-center gap-2 text-base">
          <Coins className="h-4 w-4 text-primary" /> Quẻ Kinh Dịch ({hexagrams.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {hexagrams.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {hexagrams.map((h, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1.5 rounded-full border border-gold-soft/40 bg-background/40 px-3 py-1.5 text-xs transition hover:border-primary/50"
              >
                <button
                  onClick={() => setOpenHex(h)}
                  className="inline-flex items-center gap-1.5 hover:underline"
                >
                  <Eye className="h-3 w-3 text-primary" /> Quẻ #{i + 1}: {h.que_chinh?.ten ?? "?"}
                </button>
                <button
                  onClick={() => remove.mutate(i)}
                  disabled={remove.isPending}
                  aria-label="Bỏ quẻ này"
                  className="text-muted-foreground transition hover:text-destructive"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        ) : null}
        <Button
          onClick={() => cast.mutate()}
          disabled={cast.isPending}
          variant="outline"
          className="w-full"
        >
          {cast.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Coins className="mr-2 h-4 w-4" />
          )}
          Gieo thêm một quẻ
        </Button>
        <p className="text-xs text-muted-foreground">
          Mỗi lần gieo thêm 1 quẻ vào phiên; tất cả quẻ đều được dùng làm bối cảnh khi hỏi.
        </p>
        {openHex ? <HexDetailDialog hex={openHex} onClose={() => setOpenHex(null)} /> : null}
      </CardContent>
    </Card>
  );
}

// ─── Dialog xem lá số / quẻ ────────────────────────────────────────────────

/**
 * Thu nhỏ nội dung rộng (vd lá số 640px) cho vừa bề ngang container — giữ trọn vẹn
 * thay vì cuộn ngang trên mobile. Desktop đủ rộng thì giữ nguyên kích thước (scale=1).
 */
export function ScaleToFit({ children }: { children: React.ReactNode }) {
  const outerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [height, setHeight] = useState<number | undefined>(undefined);

  useEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return;
    const update = () => {
      const avail = outer.clientWidth;
      const natural = inner.scrollWidth;
      const s = natural > avail ? avail / natural : 1;
      setScale(s);
      setHeight(inner.offsetHeight * s);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(outer);
    ro.observe(inner);
    return () => ro.disconnect();
  }, [children]);

  return (
    <div ref={outerRef} className="w-full" style={{ height }}>
      <div
        ref={innerRef}
        style={{ transform: `scale(${scale})`, transformOrigin: "top left", width: "max-content" }}
      >
        {children}
      </div>
    </div>
  );
}

function ChartDetailDialog({ chartId, onClose }: { chartId: string; onClose: () => void }) {
  const chart = useQuery({
    queryKey: ["tuvi-chart", chartId],
    queryFn: () => getTuviChart(chartId),
  });
  const c = chart.data;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="block max-h-[88vh] space-y-4 overflow-y-auto overflow-x-hidden sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="text-gold-gradient">Lá số: {c?.ho_ten ?? "…"}</DialogTitle>
        </DialogHeader>
        {chart.isLoading || !c ? (
          <Skeleton className="h-72 w-full rounded-xl" />
        ) : (
          <ScaleToFit>
            <TuviLaSoBoard apiChart={c} />
          </ScaleToFit>
        )}
      </DialogContent>
    </Dialog>
  );
}

function HexDetailDialog({ hex, onClose }: { hex: HexagramResult; onClose: () => void }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-gold-gradient">Quẻ: {hex.que_chinh?.ten ?? "?"}</DialogTitle>
        </DialogHeader>
        <div className="overflow-x-auto">
          <HexagramFullBoard hex={hex} />
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Chip gợi ý câu hỏi — bấm là gửi luôn, ẩn dần câu đã hỏi. */
function SuggestionChips({
  suggestions,
  asked,
  disabled,
  onPick,
  hideHeader = false,
}: {
  suggestions: { label: string; question: string }[];
  asked: Set<string>;
  disabled: boolean;
  onPick: (question: string) => void;
  hideHeader?: boolean;
}) {
  const remaining = suggestions.filter((s) => !asked.has(s.question.trim()));
  if (!remaining.length) return null;
  return (
    <div className="space-y-1.5">
      {!hideHeader && (
        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          Hỏi nhanh
        </p>
      )}
      <div className="flex flex-wrap gap-1.5">
        {remaining.map((s) => (
          <button
            key={s.question}
            type="button"
            disabled={disabled}
            onClick={() => onPick(s.question)}
            title={s.question}
            className="min-h-11 rounded-full border border-gold-soft/55 bg-background/50 px-3 py-1.5 text-[11px] text-foreground/85 transition hover:border-primary/60 hover:bg-primary/10 disabled:opacity-50"
          >
            {s.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function ChatPanel({
  sessionId,
  title = "Hỏi luận Bói Đê",
  emptyHint = "Hãy gắn lá số / gieo quẻ rồi đặt câu hỏi Bói Đê đầu tiên.",
  hideInitialAssistant = false,
  variant = "card",
  suggestions,
}: {
  sessionId: string;
  title?: string;
  emptyHint?: string;
  hideInitialAssistant?: boolean;
  variant?: "card" | "silk" | "palm-compact";
  /** Câu hỏi gợi ý bấm-là-gửi (bói chỉ tay sinh kèm bài luận). */
  suggestions?: { label: string; question: string }[];
}) {
  const qc = useQueryClient();
  const { refresh: refreshUser } = useBoideUser();
  const [input, setInput] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const compactMessagesRef = useRef<HTMLDivElement>(null);
  const compactInputRef = useRef<HTMLInputElement>(null);
  const shouldAutoScrollRef = useRef(false);

  const history = useQuery({
    queryKey: ["chat-history", sessionId],
    queryFn: () => getChatHistory(sessionId),
    enabled: Boolean(sessionId),
  });
  const msgs = useMemo(() => {
    const all = history.data?.messages ?? [];
    return hideInitialAssistant && all[0]?.role === "assistant" ? all.slice(1) : all;
  }, [hideInitialAssistant, history.data?.messages]);

  const send = useMutation({
    mutationFn: (message: string) =>
      sendChatMessage({ sessionId, message, fortuneType: "tong_hop" }),
    onSuccess: () => {
      refreshUser();
      qc.invalidateQueries({ queryKey: ["chat-history", sessionId] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Gửi tin thất bại"),
  });

  const rate = useChatRating(sessionId);

  const handleSend = () => {
    const text = input.trim();
    if (!text || send.isPending) return;
    shouldAutoScrollRef.current = true;
    setInput(""); // clear ngay khi gửi, không đợi response
    send.mutate(text);
  };

  const sendDirect = (question: string) => {
    const text = question.trim();
    if (!text || send.isPending) return;
    shouldAutoScrollRef.current = true;
    send.mutate(text);
  };

  // Câu đã hỏi thì bỏ khỏi danh sách gợi ý (kể cả lượt đang gửi dở).
  const askedQuestions = useMemo(() => {
    const set = new Set(msgs.filter((m) => m.role === "user").map((m) => m.content.trim()));
    if (send.isPending && typeof send.variables === "string") set.add(send.variables.trim());
    return set;
  }, [msgs, send.isPending, send.variables]);

  useEffect(() => {
    if (!shouldAutoScrollRef.current) return;
    if (variant === "palm-compact") {
      compactMessagesRef.current?.scrollTo({
        top: compactMessagesRef.current.scrollHeight,
        behavior: "smooth",
      });
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }
    if (!send.isPending) shouldAutoScrollRef.current = false;
  }, [msgs, send.isPending, variant]);

  if (variant === "palm-compact") {
    const visibleMessages = showHistory ? msgs : msgs.slice(-2);
    const olderCount = Math.max(0, msgs.length - 2);
    return (
      <section className="mt-8" data-tour="tonghop-chat" aria-label={title}>
        <SilkDivider label="ĐÀM ĐẠO" />
        <div className="mb-5 text-center">
          <p className="font-display text-[10px] font-semibold uppercase tracking-[0.22em] text-primary/75">
            Hỏi thêm về bàn tay
          </p>
          <h2 className="mt-1 font-serif-soft text-lg italic text-foreground">{title}</h2>
          <p className="mx-auto mt-1 max-w-md text-xs leading-5 text-muted-foreground">
            Thầy vẫn nhớ các vùng đã khoanh và toàn bộ lời luận của phiên này.
          </p>
        </div>
        {olderCount > 0 ? (
          <button
            type="button"
            onClick={() => setShowHistory((value) => !value)}
            aria-expanded={showHistory}
            className="mb-3 min-h-11 rounded-full border border-gold-soft/55 bg-background/35 px-3 text-xs font-semibold text-primary transition hover:border-primary/60 hover:bg-primary/8"
          >
            {showHistory ? "Ẩn câu hỏi trước" : `Xem ${olderCount} câu hỏi trước`}
          </button>
        ) : null}
        <div
          ref={compactMessagesRef}
          className="max-h-[28rem] overflow-y-auto overscroll-contain pr-1"
          aria-live="polite"
        >
          {history.isLoading ? (
            <Skeleton className="h-24 w-full rounded-md" />
          ) : visibleMessages.length === 0 ? (
            <div>
              <SilkDivider />
              <p className="px-4 text-center font-serif-soft text-sm italic leading-6 text-muted-foreground">
                {emptyHint}
              </p>
            </div>
          ) : (
            visibleMessages.map((message) =>
              message.role === "user" ? (
                <div key={message.id}>
                  <SilkDivider />
                  <div className="mb-5 rounded-md border border-gold-soft/40 bg-background/20 p-3 text-center">
                    <span className="font-display text-[9px] tracking-[0.28em] text-primary/75">
                      LỜI THỈNH GIÁO
                    </span>
                    <p className="mt-2 font-serif-soft text-sm italic leading-6 text-foreground/80">
                      “{message.content}”
                    </p>
                  </div>
                </div>
              ) : (
                <div key={message.id} className="mt-5">
                  <SilkReadingMarkdown body={message.content} density="compact" />
                  <AiWatermark />
                  <div className="mt-4 border-t border-gold-soft/25 pt-3">
                    <StarRating
                      value={message.rating ?? 0}
                      busy={rate.isPending}
                      onRate={(value) => rate.mutate({ messageId: message.id, value })}
                      comment={message.rating_comment}
                      commentRewarded={Boolean(message.rating_comment_rewarded_at)}
                      onComment={(comment) =>
                        rate.mutate({
                          messageId: message.id,
                          value: message.rating ?? null,
                          comment,
                        })
                      }
                    />
                  </div>
                </div>
              ),
            )
          )}
          {send.isPending && send.variables ? (
            <div>
              <SilkDivider />
              <div className="mb-5 rounded-md border border-gold-soft/40 bg-background/20 p-3 text-center">
                <span className="font-display text-[9px] tracking-[0.28em] text-primary/75">
                  LỜI THỈNH GIÁO
                </span>
                <p className="mt-2 font-serif-soft text-sm italic leading-6 text-foreground/80">
                  “{send.variables}”
                </p>
              </div>
            </div>
          ) : null}
          {send.isPending ? (
            <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin text-primary" /> Thầy đang luận…
            </div>
          ) : null}
        </div>
        {suggestions?.length ? (
          <div className="mt-5">
            <SilkDivider label="HỎI NHANH" />
            <SuggestionChips
              suggestions={suggestions}
              asked={askedQuestions}
              disabled={send.isPending}
              onPick={(question) => {
                setInput(clampWords(question));
                requestAnimationFrame(() => compactInputRef.current?.focus());
              }}
              hideHeader
            />
          </div>
        ) : null}
        <form
          onSubmit={(event) => {
            event.preventDefault();
            handleSend();
          }}
          className="mt-6 flex items-center gap-3 rounded-full border border-gold-soft bg-card/80 px-4 py-2 shadow-sm focus-within:border-primary/70 dark:bg-background/60 dark:shadow-gold"
        >
          <Input
            ref={compactInputRef}
            value={input}
            onChange={(event) => setInput(clampWords(event.target.value))}
            placeholder="Hỏi thêm điều bạn muốn biết…"
            className="h-9 min-w-0 flex-1 border-0 bg-transparent px-0 text-sm shadow-none placeholder:italic placeholder:text-muted-foreground/70 focus-visible:ring-0"
          />
          <Button
            type="submit"
            disabled={!input.trim() || send.isPending}
            size="icon"
            className="h-10 w-10 shrink-0 rounded-full bg-gold-gradient text-primary-foreground shadow-gold transition hover:scale-105 disabled:opacity-50"
          >
            <Send className="h-4 w-4" />
          </Button>
        </form>
      </section>
    );
  }

  if (variant === "silk") {
    return (
      <section className="flex flex-col h-full min-h-0" data-tour="tonghop-chat" aria-label={title}>
        {/* Scrollable messages and suggestion chips area */}
        <div className="flex-1 overflow-y-auto min-h-0 pr-1 pb-4 space-y-6 scrollbar-thin">
          {history.isLoading ? (
            <Skeleton className="h-24 w-full rounded-md" />
          ) : msgs.length === 0 ? (
            <div className="pt-4">
              <SilkDivider />
              <p className="px-4 text-center font-serif-soft text-sm italic leading-6 text-muted-foreground">
                {emptyHint}
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {msgs.map((m) =>
                m.role === "user" ? (
                  <div key={m.id}>
                    <SilkDivider />
                    <div className="mb-5 rounded-md border border-gold-soft/40 bg-background/20 p-3 text-center">
                      <span className="font-display text-[9px] tracking-[0.28em] text-primary/75">
                        LỜI THỈNH GIÁO
                      </span>
                      <p className="mt-2 font-serif-soft text-sm italic leading-6 text-foreground/80">
                        “{m.content}”
                      </p>
                    </div>
                  </div>
                ) : (
                  <div key={m.id} className="mt-5">
                    <SilkReadingMarkdown body={m.content} />
                    <AiWatermark />
                    <div className="mt-4 border-t border-gold-soft/25 pt-3">
                      <StarRating
                        value={m.rating ?? 0}
                        busy={rate.isPending}
                        onRate={(v) => rate.mutate({ messageId: m.id, value: v })}
                        comment={m.rating_comment}
                        commentRewarded={Boolean(m.rating_comment_rewarded_at)}
                        onComment={(comment) =>
                          rate.mutate({ messageId: m.id, value: m.rating ?? null, comment })
                        }
                      />
                    </div>
                  </div>
                ),
              )}
            </div>
          )}

          {send.isPending && send.variables ? (
            <div>
              <SilkDivider />
              <div className="mb-5 rounded-md border border-gold-soft/40 bg-background/20 p-3 text-center">
                <span className="font-display text-[9px] tracking-[0.28em] text-primary/75">
                  LỜI THỈNH GIÁO
                </span>
                <p className="mt-2 font-serif-soft text-sm italic leading-6 text-foreground/80">
                  “{send.variables}”
                </p>
              </div>
            </div>
          ) : null}
          {send.isPending ? (
            <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin text-primary" /> Thầy đang luận…
            </div>
          ) : null}
          <div ref={messagesEndRef} />
          {suggestions?.length ? (
            <div>
              <SilkDivider label="HỎI NHANH" />
              <SuggestionChips
                suggestions={suggestions}
                asked={askedQuestions}
                disabled={send.isPending}
                onPick={sendDirect}
                hideHeader
              />
            </div>
          ) : null}
        </div>

        {/* Sticky Form Input Area */}
        <form
          onSubmit={(event) => {
            event.preventDefault();
            handleSend();
          }}
          className="border-t border-gold-soft/30 bg-background/25 py-4 shrink-0"
        >
          <div className="flex items-center gap-3 rounded-full border border-gold-soft bg-card/85 px-4 py-2 shadow-sm focus-within:border-primary/70 dark:bg-background/60 dark:shadow-gold">
            <Input
              value={input}
              onChange={(e) => setInput(clampWords(e.target.value))}
              placeholder="Xin thỉnh giáo thêm với thầy…"
              className="h-9 min-w-0 flex-1 border-0 bg-transparent px-0 text-sm shadow-none placeholder:italic placeholder:text-muted-foreground/70 focus-visible:ring-0"
            />
            <Button
              type="submit"
              disabled={!input.trim() || send.isPending}
              size="icon"
              className="h-10 w-10 shrink-0 rounded-full bg-gold-gradient text-primary-foreground shadow-gold transition hover:scale-105 disabled:opacity-50"
              aria-label="Thỉnh giáo"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </form>
      </section>
    );
  }

  return (
    <Card
      className="overflow-hidden border-gold-soft/45 bg-card/55 shadow-[0_12px_36px_rgba(30,25,16,0.08)] backdrop-blur"
      data-tour="tonghop-chat"
    >
      <CardHeader className="border-b border-border/35 px-4 py-3 md:px-5">
        <CardTitle className="flex items-center gap-2 font-serif-soft text-lg text-foreground">
          <MessageSquare className="h-4 w-4 text-primary" /> {title}
        </CardTitle>
        <p className="mt-0.5 text-[11px] text-muted-foreground">
          Thầy vẫn nhớ lá số, quẻ và toàn bộ lời luận của phiên này.
        </p>
      </CardHeader>
      <CardContent className="space-y-3 p-3 md:p-4">
        <div className="max-h-[480px] space-y-3 overflow-y-auto overscroll-contain pr-1">
          {history.isLoading ? (
            <Skeleton className="h-20 w-full rounded-xl" />
          ) : msgs.length === 0 ? (
            <p className="px-4 py-2 text-center text-xs leading-5 text-muted-foreground">
              {emptyHint}
            </p>
          ) : (
            msgs.map((m) => (
              <div
                key={m.id}
                className={`flex items-end gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                {m.role === "assistant" ? (
                  <span className="mb-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-primary/50 bg-gold-gradient text-xs font-semibold text-primary-foreground">
                    T
                  </span>
                ) : null}
                <div
                  className={`max-w-[88%] rounded-2xl border px-3.5 py-2.5 text-sm shadow-sm ${m.role === "user" ? "rounded-br-md border-primary/35 bg-primary/10 text-foreground" : "rounded-bl-md border-border/45 bg-background/75 text-foreground"}`}
                >
                  {m.role === "assistant" ? (
                    <ReactMarkdown remarkPlugins={MARKDOWN_REMARK_PLUGINS} components={MD}>
                      {m.content}
                    </ReactMarkdown>
                  ) : (
                    <p className="leading-6">{m.content}</p>
                  )}
                  {m.role === "assistant" ? <AiWatermark className="mt-2" /> : null}
                  {m.role === "assistant" ? (
                    <div className="mt-2 border-t border-border/30 pt-2">
                      <StarRating
                        value={m.rating ?? 0}
                        busy={rate.isPending}
                        onRate={(v) => rate.mutate({ messageId: m.id, value: v })}
                        comment={m.rating_comment}
                        commentRewarded={Boolean(m.rating_comment_rewarded_at)}
                        onComment={(comment) =>
                          rate.mutate({ messageId: m.id, value: m.rating ?? null, comment })
                        }
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            ))
          )}
          {send.isPending && send.variables ? (
            <div className="flex justify-end">
              <div className="max-w-[88%] rounded-2xl rounded-br-md border border-primary/35 bg-primary/10 px-3.5 py-2.5 text-sm">
                {send.variables}
              </div>
            </div>
          ) : null}
          {send.isPending ? (
            <div className="mr-8 flex items-center gap-2 rounded-2xl rounded-bl-md border border-border/40 bg-background/60 px-3.5 py-3 text-sm text-muted-foreground">
              <span className="flex h-7 w-7 items-center justify-center rounded-full border border-primary/50 bg-gold-gradient text-xs font-semibold text-primary-foreground">
                T
              </span>
              <Loader2 className="h-4 w-4 animate-spin" /> Thầy đang luận…
            </div>
          ) : null}
          <div ref={messagesEndRef} />
        </div>

        {suggestions?.length ? (
          <SuggestionChips
            suggestions={suggestions}
            asked={askedQuestions}
            disabled={send.isPending}
            onPick={sendDirect}
          />
        ) : null}

        <div className="flex items-center gap-2 rounded-2xl border border-gold-soft/55 bg-background/75 p-1.5 pl-3.5 shadow-inner focus-within:border-primary/70 focus-within:ring-2 focus-within:ring-primary/10">
          <Input
            value={input}
            onChange={(e) => setInput(clampWords(e.target.value))}
            placeholder="Hỏi thêm điều bạn muốn biết…"
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSend();
            }}
            className="h-9 border-0 bg-transparent px-0 text-sm shadow-none focus-visible:ring-0"
          />
          <Button
            onClick={handleSend}
            disabled={!input.trim() || send.isPending}
            size="icon"
            className="h-9 w-9 shrink-0 rounded-full bg-gold-gradient text-primary-foreground"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
